import { List, Map } from "immutable";
import moment from "moment";
import { createSelector } from "reselect";

import { ContentType } from "@seneca/course-service-types";

// eslint-disable-next-line unused-imports/no-unused-imports
import type { areAllAssignedSectionContentsCompleted as newAllAssignedSectionContentsCompleted } from "seneca-common/features/assignments/logic/areAllAssignedSectionContentsCompleted";
// eslint-disable-next-line unused-imports/no-unused-imports
import type { getUnstudiedAssignmentQuestionIds } from "seneca-common/features/assignments/logic/exam-questions";
// eslint-disable-next-line unused-imports/no-unused-imports
import type { getUnstudiedAssignmentQuizIds } from "seneca-common/features/assignments/logic/quizzes";
// eslint-disable-next-line unused-imports/no-unused-imports
import type { getUnstudiedAssignmentWordIds } from "seneca-common/features/assignments/logic/words";
import { specTypes } from "seneca-common/features/assignments/state/models/spec/consts";
import {
  getClassAssignmentIds,
  getClassAssignments,
  getCourseAssignments,
  getNextDueIncompleteStudentAssignment
} from "seneca-common/features/assignments/state/selectors/state/assignments/assignments";
import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import Assignment from "../../../../state/models/Assignment";
import { PATH_TO_STUDENT_ASSIGNMENTS_STATE } from "../consts";
import QuestionStat from "../models/QuestionStat";
import StudentAssignment from "../models/StudentAssignment";
import StudentAssignmentsState from "../models/StudentAssignmentsState";
import { getAssignments } from "./state";
import * as studentAssignment from "./state/assignments/assignment/studentAssignment";
import * as studentAssignments from "./state/assignments/studentAssignments";

type State = any;
type AssignmentId = string;
type CourseId = string;
type SectionId = string;
type ContentId = string;
type QuizId = string;
type QuestionId = string;

export function getStudentAssignmentsState(
  state: State
): StudentAssignmentsState {
  return state.get(PATH_TO_STUDENT_ASSIGNMENTS_STATE);
}

const getStateAssignments: (
  arg0: State
) => Map<AssignmentId, StudentAssignment> = composeSelector(
  getStudentAssignmentsState,
  getAssignments
);

export function getAllAssignments(state: State) {
  return getStateAssignments(state);
}

type GetAllAssignmentIdsType = (arg0: any) => List<AssignmentId>;
export const getAllAssignmentIds: GetAllAssignmentIdsType = createSelector(
  getStateAssignments,
  assignments => assignments.keySeq().toList()
);

export const makeGetClassAssignmentIds = (classId: string) => {
  return createSelector(getAllAssignments, assignments =>
    getClassAssignmentIds(assignments as any, classId)
  );
};

// @ts-ignore
export const getAssignment: (
  arg0: State,
  arg1: AssignmentId
) => StudentAssignment = composeSelector(
  // @ts-ignore
  getStateAssignments,
  studentAssignments.getAssignment
);

export const assignmentExists: (State: any, AssignmentId: any) => boolean =
  composeSelector(
    getStateAssignments,
    (state, assignmentId) =>
      !!studentAssignments.getAssignment(state as any, assignmentId)
  );

export const getAssignmentClassId: (State: any, AssignmentId: any) => string =
  composeSelector(
    getStateAssignments as any,
    studentAssignments.getAssignmentClassId
  );

export const getAssignmentStartDate: (State: any, AssignmentId: any) => string =
  composeSelector(
    getStateAssignments as any,
    studentAssignments.getAssignmentStartDate
  );

export const getAssignmentDueDate: (State: any, AssignmentId: any) => string =
  composeSelector(
    getStateAssignments as any,
    studentAssignments.getAssignmentDueDate
  );

export const getAssignmentStatus: (State: any, AssignmentId: any) => string =
  composeSelector(
    getStateAssignments as any,
    studentAssignments.getAssignmentStatus
  );

export const getAssignmentName: (State: any, AssignmentId: any) => string =
  composeSelector(
    getStateAssignments as any,
    studentAssignments.getAssignmentName
  );

export const getAssignmentSpecType = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentSpecType
);

export const getAssignmentSpec = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentSpec
);

export const isAssignmentQuizzesOnly = composeSelector(
  getStateAssignments as any,
  studentAssignments.isAssignmentQuizzesOnly
);

export const getAssignmentCourseId: (
  State: any,
  AssignmentId: any
) => CourseId = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentCourseId
);

export const getAssignmentQuizTitle: (
  State: any,
  AssignmentId: any
) => string | null = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentQuizTitle
);

export const getAssignmentSectionIds: (
  State: any,
  AssignmentId: any
) => List<SectionId> = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentSectionIds
);

export const getAssignmentContentIds: (
  State: any,
  AssignmentId: string
) => List<ContentId> = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentContentIds
);

export const getAssignmentContentIdsForSection: (
  State: any,
  AssignmentId: string,
  SectionId: string
) => List<ContentId> = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentContentIdsForSection
);

export const getAssignmentQuizIds: (
  State: any,
  AssignmentId: any
) => List<QuizId> = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentQuizIds
);

export const getAssignmentWordIds: (
  State: any,
  AssignmentId: any
) => List<string> = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentWordIds
);

export const getAssignmentNumberOfSections: (
  State: any,
  AssignmentId: any
) => number = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentNumberOfSections
);

export const getAssignmentCutoffDate: (
  State: any,
  AssignmentId: any
) => string = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentCutoffDate
);

export const getAssignmentExamQuestionIds: (
  State: any,
  AssignmentId: AssignmentId
) => List<string> = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentExamQuestionIds
);

export const getAssignmentExamQuestionStat: (
  State: any,
  AssignmentId: AssignmentId,
  QuestionId: QuestionId
) => QuestionStat | undefined = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentQuestionStat
);

export const getAssignmentExamQuestionMark = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentQuestionMark
);

export const getAssignmentExamQuestionMarksTotal = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentQuestionMarksTotal
);
export const getAssignmentQuizBestMarks = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentQuizBestMarks
);

export const getAssignmentQuizAverageBestScore = (assignmentId: string) =>
  createSelector(getStateAssignments, (assignments: any) => {
    const assignment = studentAssignments.getAssignment(
      assignments,
      assignmentId
    );
    const quizIds = studentAssignment.getStudiedQuizIds(assignment as any);
    return (
      quizIds.reduce(
        (total, quizId) =>
          total +
          studentAssignments.getAssignmentQuizModulesAverageBestMarks(
            assignments,
            assignmentId,
            quizId
          ),
        0
      ) / quizIds.size
    );
  });

export const getAssignmentQuizMarksTotal = composeSelector(
  getStateAssignments as any,
  studentAssignments.getAssignmentQuizMarksTotal
);

export const getTotalAssignmentQuizMarksTotal = (assignmentId: string) =>
  createSelector(getStateAssignments, (assignments: any) => {
    const quizIds = studentAssignments.getAssignmentQuizIds(
      assignments,
      assignmentId
    );
    return quizIds.reduce(
      (acc, quizId) =>
        acc +
        (studentAssignments.getAssignmentQuizMarksTotal(
          assignments,
          assignmentId,
          quizId
        ) ?? 0),
      0
    );
  });

export const isAssignmentExamQuestionFlagged = composeSelector(
  getStateAssignments as any,
  studentAssignments.isAssignmentQuestionFlagged
);

export const assignmentIncludesExamQuestions: (
  State: any,
  AssignmentId: string
) => boolean = composeSelector(
  getStateAssignments as any,
  studentAssignments.assignmentIncludesExamQuestions
);

export const makeGetAssignmentAggregatedQuestionsMarks = (
  assignmentId: string
) => {
  return createSelector(getStateAssignments, assignments => {
    return studentAssignments.getAssignmentAggregatedQuestionsMarks(
      assignments,
      assignmentId
    );
  });
};

/**
 * @deprecated consider using getUnstudiedAssignmentQuestionIds
 * {@link getUnstudiedAssignmentQuestionIds}
 */
export const makeGetUnstudiedQuestionIds = (assignmentId: string) => {
  return createSelector(getStateAssignments, assignments => {
    const assignedQuestionIds = studentAssignments.getAssignmentExamQuestionIds(
      assignments as any,
      assignmentId
    );
    return assignedQuestionIds.filter(
      id =>
        !studentAssignments.questionHasBeenStudied(
          assignments,
          assignmentId,
          id
        )
    );
  });
};

/**
 * @deprecated consider using getUnstudiedAssignmentQuizIds
 * {@link getUnstudiedAssignmentQuizIds}
 */
export const makeGetUnstudiedQuizIds = (assignmentId: string) => {
  return createSelector(getStateAssignments, assignments => {
    const assignedQuizIds = studentAssignments.getAssignmentQuizIds(
      assignments as any,
      assignmentId
    );
    return assignedQuizIds.filter(
      id =>
        !studentAssignments.quizHasBeenStudied(assignments, assignmentId, id)
    );
  });
};

export const makeGetStudiedQuizIds = (assignmentId: string) => {
  return createSelector(getStateAssignments, assignments => {
    const assignedQuizIds = studentAssignments.getAssignmentQuizIds(
      assignments as any,
      assignmentId
    );
    return assignedQuizIds.filter(id =>
      studentAssignments.quizHasBeenStudied(assignments, assignmentId, id)
    );
  });
};

/**
 * @deprecated consider using getUnstudiedAssignmentWordIds
 * {@link getUnstudiedAssignmentWordIds}
 */
export const makeGetUnstudiedWordIds = (assignmentId: string) => {
  return createSelector(getStateAssignments, assignments => {
    const assignedWordIds = studentAssignments.getAssignmentWordIds(
      assignments as any,
      assignmentId
    );
    return assignedWordIds.filter(
      id =>
        !studentAssignments.wordHasBeenStudied(assignments, assignmentId, id)
    );
  });
};

export const makeGetStudiedWordIds = (assignmentId: string) => {
  return createSelector(getStateAssignments, assignments => {
    const assignedWordIds = studentAssignments.getAssignmentWordIds(
      assignments as any,
      assignmentId
    );
    return assignedWordIds.filter(id =>
      studentAssignments.wordHasBeenStudied(assignments, assignmentId, id)
    );
  });
};

export const hasMoreThanOneAssignment: (arg0: State) => boolean =
  composeSelector(
    getStateAssignments as any,
    studentAssignments.hasMoreThanOneAssignment
  );

export const getNumberOfUpcomingAssignments: (arg0: State) => number =
  composeSelector(
    getStateAssignments as any,
    studentAssignments.getNumberOfUpcomingAssignments
  );

export const makeGetNextDueAssignmentId = (now: moment.Moment) => {
  return createSelector(getAllAssignments, assignments => {
    return studentAssignments.getNextDueAssignmentId(assignments as any, now);
  });
};

function makeGetAssignmentsForCourse(currentCourseId: string) {
  return createSelector(getAllAssignments, allAssignments =>
    allAssignments.reduce((acc, assignment) => {
      const {
        // @ts-ignore
        spec: { courseId },
        id
      } = assignment;
      if (courseId === currentCourseId) {
        return acc.set(id, assignment);
      }
      return acc;
    }, Map())
  );
}

export function makeGetFutureAssignments(now: moment.Moment) {
  return createSelector(getAllAssignments, assignments => {
    return studentAssignments
      .getFutureAssignments(assignments as any, now)
      .sortBy(studentAssignment.getDueDate);
  });
}

export function makeGetFutureAssignmentsForCourse(
  currentCourseId: string,
  now: moment.Moment
) {
  const getAssignmentsForCourse = makeGetAssignmentsForCourse(currentCourseId);

  return createSelector(getAssignmentsForCourse, assignmentsForCourse => {
    return studentAssignments
      .getFutureAssignments(assignmentsForCourse as any, now)
      .sortBy(studentAssignment.getDueDate);
  });
}

export function makeGetFutureAssignmentIdsForCourse(
  currentCourseId: string,
  now: moment.Moment
) {
  const getAssignmentsForCourse = makeGetFutureAssignmentsForCourse(
    currentCourseId,
    now
  );

  return createSelector(getAssignmentsForCourse, assignmentsForCourse => {
    return assignmentsForCourse.map(studentAssignment.getAssignmentId);
  });
}

export function makeCourseHasFutureAsssignments(
  currentCourseId: string,
  now: moment.Moment
) {
  const getAssignmentsForCourse = makeGetFutureAssignmentsForCourse(
    currentCourseId,
    now
  );

  return createSelector(
    getAssignmentsForCourse,
    assignmentsForCourse => assignmentsForCourse.size > 0
  );
}

export function makeGetNextDueAssignmentForCourse(
  currentCourseId: string,
  now: moment.Moment
) {
  const getFutureAssignmentsForCourse =
    makeGetAssignmentsForCourse(currentCourseId);

  return createSelector(
    getFutureAssignmentsForCourse,
    futureAssignmentsForCourse => {
      return getNextDueIncompleteStudentAssignment(
        futureAssignmentsForCourse as any,
        now
      );
    }
  );
}

export const makeGetAssignmentsForSection = (sectionId: string) =>
  createSelector(getAllAssignments, allAssignments =>
    allAssignments.reduce((map, assignment) => {
      const {
        id,
        spec: { sectionIds }
      } = assignment as any;
      return sectionIds?.includes(sectionId) ? map.set(id, assignment) : map;
    }, Map<AssignmentId, StudentAssignment>())
  );

export const makeGetAssignmentsForQuiz = (quizId: string) =>
  createSelector(getAllAssignments, allAssignments =>
    allAssignments.reduce((map, assignment) => {
      const {
        id,
        spec: { quizzes }
      } = assignment as any;
      return quizzes?.get(quizId) ? map.set(id, assignment) : map;
    }, Map<AssignmentId, StudentAssignment>())
  );

export const makeGetNextDueAssignmentIdForSection = (
  sectionId: string,
  now: moment.Moment
) =>
  createSelector(
    makeGetAssignmentsForSection(sectionId),
    futureAssignmentsForSection =>
      studentAssignments.getNextDueAssignmentId(
        futureAssignmentsForSection as any,
        now
      )
  );

export const makeGetNextDueAssignmentIdForSectionOrCourse = ({
  sectionId,
  courseId,
  now
}: {
  sectionId: string;
  courseId: string;
  now: moment.Moment;
}) =>
  createSelector(
    makeGetNextDueAssignmentIdForSection(sectionId, now),
    makeGetNextDueAssignmentForCourse(courseId, now),
    (nextDueAssignmentIdForSection, nextDueAssignmentIdForCourse) =>
      nextDueAssignmentIdForSection || nextDueAssignmentIdForCourse
  );

export const makeIsFutureAssignmentStartDate = (
  assignmentId: string,
  now: moment.Moment
) =>
  createSelector(getStateAssignments, assignments =>
    now.isBefore(
      studentAssignments.getAssignmentStartDate(
        assignments as any,
        assignmentId
      )
    )
  );

export const makeIsAssignmentAfterCutoffDate = (
  assignmentId: string,
  now: moment.Moment
) =>
  createSelector(getStateAssignments, assignments =>
    now.isAfter(
      moment(
        studentAssignments.getAssignmentCutoffDate(
          assignments as any,
          assignmentId
        )
      )
    )
  );

export const getAssignmentSectionBestScore = (
  state: State,
  assignmentId: string,
  sectionId: string,
  contentType?: ContentType
) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getAssignmentSectionBestScore(
    assignment,
    sectionId,
    contentType
  );
};

export const getAssignmentContentTypesBySectionId = (
  state: State,
  assignmentId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getAssignmentContentTypesBySectionId(assignment);
};

export const getAssignmentHasOnlyHardestQuestionsContent = (
  state: State,
  assignmentId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  const contentTypesBySectionId =
    studentAssignment.getAssignmentContentTypesBySectionId(assignment);

  return !contentTypesBySectionId
    ?.valueSeq()
    .flatten()
    .some(contentType => contentType === ContentType.standard);
};

export const getAssignmentContentTypesForSectionId = (
  state: State,
  assignmentId: string,
  sectionId: string
) => {
  return getAssignmentContentTypesBySectionId(state, assignmentId)?.get(
    sectionId
  );
};

export const getAssignmentStudiedSectionsAverageBestScore = (
  state: State,
  assignmentId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  const sectionIds = studentAssignment.getStudiedSectionIds(assignment);

  const scores = sectionIds
    .map(sectionId => {
      return studentAssignment.getAssignmentSectionBestScore(
        assignment,
        sectionId
      );
    })
    .flatten() as List<number>;

  return scores.reduce((p, c) => p + c, 0) / scores.size;
};

export const getAssignmentTotalStudyTime = (
  state: State,
  assignmentId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getAssignmentTotalStudyTime(assignment);
};

export const getAssignmentTeacherName = (
  state: State,
  assignmentId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getAssignmentTeacherName(assignment);
};

export const getAssignmentUserId = (state: State, assignmentId: string) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getAssignmentUserId(assignment);
};

export const sectionHasBeenStudied: (
  State: any,
  AssignmentId: any,
  SectionId: any
) => boolean = composeSelector(
  getStateAssignments,
  studentAssignments.sectionHasBeenStudied
);

export const sectionContentHasBeenStudied: (
  State: any,
  AssignmentId: any,
  SectionId: any,
  contentType: any
) => boolean = composeSelector(
  getStateAssignments,
  studentAssignments.sectionContentHasBeenStudied
);

export const questionHasBeenStudied: (
  State: any,
  AssignmentId: any,
  SectionId: any
) => boolean = composeSelector(
  getStateAssignments,
  studentAssignments.questionHasBeenStudied
);

export const quizHasBeenStudied: (
  State: any,
  AssignmentId: any,
  QuizId: any
) => boolean = composeSelector(
  getStateAssignments,
  studentAssignments.quizHasBeenStudied
);

export const getAssignmentProgress = (state: State, assignmentId: string) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getAssignmentProgress(assignment);
};

export const getStudiedSectionIds = (state: State, assignmentId: string) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getStudiedSectionIds(assignment);
};

export const getStudiedSectionContents = (
  state: State,
  assignmentId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getStudiedSectionContents(assignment);
};

export const getNumberOfSectionsCompleted = (
  state: State,
  assignmentId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  return (studentAssignment as any).getNumberOfSectionsCompleted(assignment);
};

export const getNumberOfQuestionsCompleted = (
  state: State,
  assignmentId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  return (studentAssignment as any).getNumberOfQuestionsCompleted(assignment);
};

export const isLastSectionToFinishAssignment = (
  state: State,
  assignmentId: string,
  sectionId: string,
  hasHardestQuestionContent: boolean
) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.isLastSectionToFinishAssignment(
    assignment,
    sectionId,
    hasHardestQuestionContent
  );
};

export const isLastQuizToFinishAssignment = (
  state: State,
  assignmentId: string,
  quizId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  return (studentAssignment as any).isLastQuizToFinishAssignment(
    assignment,
    quizId
  );
};

export const isLastWordToFinishAssignment = (
  state: State,
  assignmentId: string,
  wordId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.isLastWordToFinishAssignment(assignment, wordId);
};

export function makeGetAssignmentsForUser(userId: string) {
  return createSelector(getAllAssignments, assignments =>
    assignments
      .toList()
      .filter(
        assignment =>
          studentAssignment.getAssignmentUserId(assignment) === userId
      )
  );
}

/**
 * @deprecated consider using newAllAssignedSectionContentsCompleted
 * {@link newAllAssignedSectionContentsCompleted}
 */
export function allAssignedSectionContentsCompleted(
  state: any,
  assignmentId: string
) {
  const assignment = getAssignment(state, assignmentId);
  const hasContentSpec = getHasContentSpec(state, assignmentId);

  return hasContentSpec
    ? studentAssignment.allContentsCompleted(assignment)
    : studentAssignment.allSectionsCompleted(assignment);
}

export function allAssignedSectionFreeContentsCompleted(
  state: any,
  assignmentId: string
) {
  const assignment = getAssignment(state, assignmentId);
  const hasContentSpec = getHasContentSpec(state, assignmentId);

  return hasContentSpec
    ? studentAssignment.allFreeContentsCompleted(assignment)
    : studentAssignment.allSectionsCompleted(assignment);
}

export function allAssignedQuizzesCompleted(state: any, assignmentId: string) {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.allQuizzesCompleted(assignment);
}

export function allAssignedWordsCompleted(state: any, assignmentId: string) {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.allWordsCompleted(assignment);
}

export function getStudiedQuestionIds(state: any, assignmentId: string) {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getStudiedQuestionIds(assignment);
}

export function allAssignedExamQuestionsCompleted(
  state: any,
  assignmentId: string
) {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.allExamQuestionsCompleted(assignment);
}

export function assignmentIsCompleted(state: any, assignmentId: string) {
  const sectionContentsAreCompleted = allAssignedSectionContentsCompleted(
    state,
    assignmentId
  );

  const examQuestionsCompleted = assignmentIncludesExamQuestions(
    state,
    assignmentId
  )
    ? allAssignedExamQuestionsCompleted(state, assignmentId)
    : true;

  const quizzesCompleted = allAssignedQuizzesCompleted(state, assignmentId);

  const wordsCompleted = allAssignedWordsCompleted(state, assignmentId);

  return (
    sectionContentsAreCompleted &&
    examQuestionsCompleted &&
    quizzesCompleted &&
    wordsCompleted
  );
}

export function getHasContentSpec(state: any, assignmentId: string) {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getHasContentSpec(assignment);
}

export function getNumberOfItemsStudied(state: any, assignmentId: string) {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getNumberOfItemsStudied(assignment);
}

export function getHasStartedAssignment(state: any, assignmentId: string) {
  return getNumberOfItemsStudied(state, assignmentId) > 0;
}

export function getNumberOfItemsAssigned(state: any, assignmentId: string) {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getNumberOfItemsAssigned(assignment);
}

export function getNumberOfUnstudiedItems(state: any, assignmentId: string) {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getNumberOfUnstudiedItems(assignment);
}

export const getNumberOfSessionsAssigned = (
  state: State,
  assignmentId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getNumberOfSessionsAssigned(assignment);
};

export const getNumberOfSessionsStudied = (
  state: State,
  assignmentId: string
) => {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getNumberOfSessionsStudied(assignment);
};

export function makeGetCourseAssignments(courseId: string) {
  return createSelector(getAllAssignments, assignments =>
    assignments ? getCourseAssignments(assignments as any, courseId) : null
  );
}

export function makeGetClassAssignments(classId: string) {
  return createSelector(getAllAssignments, assignments =>
    assignments ? getClassAssignments(assignments as any, classId) : null
  );
}

export function getAssignmentQuizModulesWithTitles(
  state: State,
  assignmentId: string,
  quizId: string
) {
  return studentAssignment.getAssignmentQuizModulesWithTitles(
    getAssignment(state, assignmentId),
    quizId
  );
}

export function assignmentQuizDetailsNeedsFetching(
  state: State,
  assignmentId: string
) {
  const assignment = getAssignment(state, assignmentId) as Assignment;

  if (!assignment || !studentAssignment.isQuizzesOnly(assignment)) {
    return false;
  }

  const quizDetails = studentAssignment.getAssignmentQuizDetails(assignment);
  const quizIds = studentAssignments.getQuizIds(assignment);

  return quizIds.some(quizId => !quizDetails.get(quizId));
}

export function getAssignmentWordBestScore(
  state: State,
  assignmentId: string,
  wordId: string
) {
  const assignment = getAssignment(state, assignmentId);
  return studentAssignment.getAssignmentWordBestScore(assignment, wordId);
}

export const getAssignmentVocabularyAverageBestScore = (
  assignmentId: string
) => {
  return createSelector(getStateAssignments, (assignments: any) => {
    const assignment = studentAssignments.getAssignment(
      assignments,
      assignmentId
    ) as any;
    const wordIds = studentAssignment.getStudiedWordIds(assignment);
    return (
      wordIds.reduce((acc, wordId) => {
        return (
          acc +
          (studentAssignment.getAssignmentWordBestScore(assignment, wordId) ||
            0)
        );
      }, 0) / wordIds.size
    );
  });
};

export const wordHasBeenStudied: (
  state: State,
  assignmentId: string,
  wordId: string
) => boolean = composeSelector(
  getStateAssignments,
  studentAssignments.wordHasBeenStudied
);

export function isVocabularyAssignment(state: State, assignmentId: string) {
  const assignment = getAssignment(state, assignmentId) as Assignment;

  return assignment.specType === specTypes.vocabulary;
}
